import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { ClientVehiclePicker } from "@/components/ClientVehiclePicker";
import { fmtMoney } from "@/lib/format";

export function CounterSaleDialog({ open, onOpenChange, onCreated }) {
  const navigate = useNavigate();
  const { data: parts } = useFetch(() => api.get("/parts"), [], { enabled: open });
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState("pick");
  const [lines, setLines] = useState([{ part_id: "", qty: 1 }]);
  const [busy, setBusy] = useState(false);
  const requestKey = useMemo(() => (open ? `cs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` : null), [open]);
  const byId = Object.fromEntries((parts || []).map((p) => [p.id, p]));
  const total = lines.reduce((s, l) => s + (byId[l.part_id]?.sale_price || 0) * Number(l.qty || 0), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/counter-sale", { client_id: clientId, lines: lines.filter((l) => l.part_id).map((l) => ({ part_id: l.part_id, qty: Number(l.qty) })), request_key: requestKey });
      toast.success(`Vente comptoir ${data.number} créée — stock déduit, à finaliser puis encaisser`);
      onOpenChange(false); setLines([{ part_id: "", qty: 1 }]); setClientId("");
      onCreated?.(); navigate(`/factures?open=${data.id}`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="counter-sale-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-primary" /> Vente de pièces au comptoir</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <ClientVehiclePicker prefix="counter-sale" clientId={clientId} vehicleId="none" onClientChange={setClientId} onVehicleChange={() => {}} mode={mode} setMode={setMode} clientLabel="Client" vehicleLabel="" withVehicle={false} />
          {mode === "pick" && (
            <div>
              <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold">Pièces vendues</span><Button size="sm" variant="outline" onClick={() => setLines((l) => [...l, { part_id: "", qty: 1 }])} data-testid="counter-sale-add-line"><Plus className="mr-1 h-3 w-3" /> Ligne</Button></div>
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className="grid gap-2 sm:grid-cols-[1fr_90px_110px_40px]" data-testid={`counter-sale-line-${i}`}>
                    <Select value={l.part_id} onValueChange={(v) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, part_id: v } : x)))}><SelectTrigger data-testid={`counter-sale-part-${i}`}><SelectValue placeholder="Pièce du stock" /></SelectTrigger><SelectContent>{(parts || []).map((p) => <SelectItem key={p.id} value={p.id} disabled={(p.available_qty ?? p.stock_qty) <= 0}>{p.sku} · {p.name} · {fmtMoney(p.sale_price)} · dispo {p.available_qty ?? p.stock_qty}</SelectItem>)}</SelectContent></Select>
                    <Input type="number" min={0.01} step="0.01" value={l.qty} onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} data-testid={`counter-sale-qty-${i}`} />
                    <div className="grid place-items-end text-sm font-semibold tabular">{fmtMoney((byId[l.part_id]?.sale_price || 0) * Number(l.qty || 0))}</div>
                    <Button variant="ghost" size="icon" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} aria-label="Retirer"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-right text-sm">Total HTVA : <strong className="tabular" data-testid="counter-sale-total">{fmtMoney(total)}</strong></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={busy || !clientId || !lines.some((l) => l.part_id)} data-testid="counter-sale-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Créer la facture comptoir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
